import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * The three way unsaved changes guard, task: unsaved changes guard
 * (2026-09-08). Mounted once, near the top of the authenticated app
 * (App.jsx), so it can intercept navigation triggered from anywhere: the
 * bottom nav's Home/+/More (and everything inside More), and each form
 * page's own back arrow and Cancel button.
 *
 * Deliberately not a data router migration. useUnsavedChangesGuard.js's own
 * header already notes that reliably blocking the browser's hardware back
 * button or an edge swipe gesture needs createBrowserRouter, a bigger
 * change than this. Every trigger actually named for this guard (Home,
 * plus, More, the back arrow, Cancel) is an in-app control this app
 * already renders, not raw browser history, so gating those specific
 * clicks is enough without that migration.
 *
 * One page is "current" at a time: whichever form last called setGuard
 * with isDirty. A page must clear its own guard on unmount (the effect
 * cleanup calling setGuard(false, null)) so a guard from a page the rep
 * already left never blocks navigation on the next one.
 */
const NavigationGuardContext = createContext(null);

export function NavigationGuardProvider({ children }) {
  const guardRef = useRef({ isDirty: false, onSave: null });
  const pendingActionRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Registered by whichever form is currently mounted and dirty. onSave
   * must resolve to a truthy value on success, false (or throw) on failure:
   * "Save and leave" only actually leaves when the save really worked, the
   * same reasoning a plain Save button already used. A truthy result can
   * be a plain `true` (EditContact.jsx's doSave) or
   * `{ mode: 'saved' | 'queued', ... }` (NewMeeting.jsx's performSave,
   * task: offline capture outbox); handleSaveAndLeave below reads `.mode`
   * when present and falls back to a generic message otherwise, so this
   * stays a real confirmation either way rather than the silent navigate
   * it used to be, which left a rep with no way to tell "saved" from
   * "lost" after tapping Save and leave.
   */
  const setGuard = useCallback((isDirty, onSave) => {
    guardRef.current = { isDirty, onSave };
  }, []);

  /**
   * Every nav trigger calls this instead of navigating directly. Fires the
   * action immediately when nothing is dirty, so a page with no real
   * changes navigates away with no prompt at all.
   */
  const guardedAction = useCallback((action) => {
    if (guardRef.current.isDirty) {
      pendingActionRef.current = action;
      setDialogOpen(true);
    } else {
      action();
    }
  }, []);

  const runPending = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setDialogOpen(false);
    action?.();
  };

  const handleSaveAndLeave = async () => {
    setSaving(true);
    try {
      const result = await guardRef.current.onSave?.();
      if (result) {
        toast.success(
          result.mode === 'queued'
            ? "Saved on this phone. Will sync once you're back online."
            : 'Contact saved.',
        );
        runPending();
      }
      // A falsy result means the save failed; onSave itself already showed
      // why (performSave/doSave's own toast.error), so nothing more to say
      // here. The dialog stays open and nothing navigates, the same as
      // tapping Save and having it fail on the form directly.
    } finally {
      setSaving(false);
    }
  };

  const handleLeaveWithoutSaving = () => runPending();

  const handleStay = () => {
    pendingActionRef.current = null;
    setDialogOpen(false);
  };

  return (
    <NavigationGuardContext.Provider value={{ setGuard, guardedAction }}>
      {children}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleStay(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>You have changes on this page that have not been saved.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button onClick={handleSaveAndLeave} disabled={saving} className="w-full bg-accent hover:bg-accent/90 text-accent-foreground">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save and leave'}
            </Button>
            <Button variant="outline" onClick={handleLeaveWithoutSaving} disabled={saving} className="w-full">
              Leave without saving
            </Button>
            <Button variant="ghost" onClick={handleStay} disabled={saving} className="w-full">
              Stay on this page
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  return ctx;
}
